import { useState } from 'react';
import { Send, Plus } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectUser } from '@/store/slices/authSlice';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/cn';

const MOCK_ROOMS = [
  { id: 'room-1', name: 'Engineering', type: 'DEPARTMENT', unread: 3, last_message: 'Deploy looks good 🚀', time: '10:42', members: 6 },
  { id: 'room-2', name: 'HR Announcements', type: 'ANNOUNCEMENT', unread: 0, last_message: 'Q2 appraisal starts next week', time: 'Yesterday', members: 15 },
  { id: 'room-3', name: 'Arjun Patel', type: 'DIRECT', unread: 1, last_message: 'Can I take Friday off?', time: '09:15', members: 2 },
  { id: 'room-4', name: 'Sales Team', type: 'DEPARTMENT', unread: 0, last_message: 'Monthly targets achieved!', time: 'Mon', members: 4 },
];

const MOCK_MESSAGES = {
  'room-1': [
    { id: 'm1', sender: 'Karthik S', time: '10:30', text: 'The new auth flow is ready for review' },
    { id: 'm2', sender: 'Priya Mehta', time: '10:35', text: 'Reviewed — looks good, merging now' },
    { id: 'm3', sender: 'Karthik S', time: '10:42', text: 'Deploy looks good 🚀' },
  ],
  'room-3': [
    { id: 'm1', sender: 'Arjun Patel', time: '09:15', text: 'Can I take Friday off?' },
  ],
};

export default function ChatPage() {
  const user = useSelector(selectUser);
  const [activeRoom, setActiveRoom] = useState(MOCK_ROOMS[0]);
  const [input, setInput] = useState('');

  const messages = MOCK_MESSAGES[activeRoom?.id] || [];

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4 fade-in">
      {/* Rooms sidebar */}
      <div className="w-64 shrink-0 hidden sm:flex flex-col card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[var(--text-primary)]">Messages</p>
          <button className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><Plus size={14}/></button>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-[var(--border)]">
          {MOCK_ROOMS.map(room=>(
            <button key={room.id} onClick={()=>setActiveRoom(room)}
              className={cn('w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                activeRoom?.id===room.id ? 'bg-[var(--accent-muted)]' : 'hover:bg-[var(--bg-muted)]'
              )}>
              <div className="w-9 h-9 rounded-full bg-[var(--accent)] flex items-center justify-center text-white text-[12px] font-bold shrink-0">
                {room.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate">{room.name}</p>
                  <span className="text-[10px] text-[var(--text-tertiary)] shrink-0 ml-2">{room.time}</span>
                </div>
                <p className="text-[11px] text-[var(--text-secondary)] truncate">{room.last_message}</p>
              </div>
              {room.unread > 0 && (
                <span className="w-4 h-4 rounded-full bg-[var(--accent)] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{room.unread}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col card p-0 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[var(--border)] flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--accent)] flex items-center justify-center text-white text-[12px] font-bold">
            {activeRoom?.name[0]}
          </div>
          <div>
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">{activeRoom?.name}</p>
            <p className="text-[11px] text-[var(--text-tertiary)]">{activeRoom?.members} members</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map(m=>{
            const isMe = m.sender === user?.name;
            return (
              <div key={m.id} className={cn('flex gap-2.5', isMe && 'flex-row-reverse')}>
                <Avatar name={m.sender} size="sm"/>
                <div className={cn('max-w-[70%]', isMe && 'items-end flex flex-col')}>
                  {!isMe && <p className="text-[11px] text-[var(--text-tertiary)] mb-1">{m.sender}</p>}
                  <div className={cn('px-3 py-2 rounded-[12px] text-[13px]',
                    isMe ? 'bg-[var(--accent)] text-white rounded-tr-[4px]' : 'bg-[var(--bg-muted)] text-[var(--text-primary)] rounded-tl-[4px]'
                  )}>
                    {m.text}
                  </div>
                  <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{m.time}</p>
                </div>
              </div>
            );
          })}
          {messages.length===0 && (
            <div className="flex items-center justify-center h-full text-[13px] text-[var(--text-tertiary)]">No messages yet. Say hi!</div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-[var(--border)] flex items-center gap-2">
          <input
            value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{ if(e.key==='Enter' && input.trim()) setInput(''); }}
            placeholder="Type a message…"
            className="flex-1 text-[13px] border border-[var(--border)] rounded-[10px] px-4 py-2.5 bg-[var(--bg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
          />
          <button
            onClick={()=>setInput('')}
            disabled={!input.trim()}
            className="w-9 h-9 rounded-[10px] bg-[var(--accent)] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--accent-hover)] transition-colors">
            <Send size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}
